import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BackendService, Base } from '@wilson/base';
import { ConfigOptions, ConfigService } from '@wilson/config';
import {
  CertificateQualification,
  CountryQualification,
  DriverLicenseQualification,
  FurtherEducationQualification,
  LanguageQualification,
  LocationQualification,
  MedicalExaminationQualification,
  OtherQualification,
  QualificationCategoryIdsMap,
  RouteQualification,
  SafetyCertificateQualification,
  TrainingQualification,
  UserQualification,
  UserQualifications,
  VehicleQualification,
} from '@wilson/interfaces';
import { QualificationsService } from '@wilson/qualifications';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SharedUserQualificationsGateway extends BackendService<
  {
    userId: string;
    partnershipId: string;
  } & Base
> {
  protected readonly http = inject(HttpClient);
  protected readonly config = inject<ConfigOptions>(ConfigService);
  private readonly translate = inject(TranslateService);
  private readonly qualificationsService = inject(QualificationsService);

  protected readonly path = 'shared-users';

  getUserQualifications(id?: string) {
    return this.http.get<UserQualification[]>(
      `${this.config.host}/${this.path}/${id}/qualifications`,
    );
  }

  public async initUserQualification(
    qualificationId: string,
    qualificationCategoryId: string,
  ): Promise<{
    userQualification: UserQualifications;
    qualificationHistory: UserQualifications[];
    qualificationName: string;
  }> {
    const relations = ['executedBy'];

    let userQualification: UserQualifications;
    let qualificationHistory: UserQualifications[] = [];
    let qualificationName = '';

    if (qualificationCategoryId === QualificationCategoryIdsMap.Country) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'country'] },
        ),
      );
      qualificationName = this.translate.instant(
        `country.${
          (userQualification as CountryQualification).country.countryCode
        }`,
      );
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.Certificate
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'certificate'] },
        ),
      );
      qualificationName = this.translate.instant(
        `certificate.${
          (userQualification as CertificateQualification).certificate.name
        }`,
      );
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.Vehicle
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'vehicleModel'] },
        ),
      );

      qualificationName = (userQualification as VehicleQualification)
        .vehicleModel.name;
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.Language
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'language'] },
        ),
      );
      qualificationName = this.translate.instant(
        `language.${
          (userQualification as LanguageQualification).language.languageCode
        }`,
      );
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.Location
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'geoLocation'] },
        ),
      );
      qualificationName = (userQualification as LocationQualification)
        .geoLocation.name;
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.Training
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'training'] },
        ),
      );

      if ((userQualification as TrainingQualification).trainingId) {
        qualificationName = this.translate.instant(
          `training.${
            (userQualification as TrainingQualification).training.name
          }`,
        );
      }
    } else if (qualificationCategoryId === QualificationCategoryIdsMap.Route) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'route'] },
        ),
      );
      if ((userQualification as RouteQualification).route.name) {
        const routes: string[] = (
          userQualification as RouteQualification
        ).route.name.split(' - ');
        qualificationName = `${routes[0]} - ${routes[routes.length - 1]}`;
      }
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.DriverLicense
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'driverLicense'] },
        ),
      );
      qualificationName = this.translate.instant(
        `driverLicense.${
          (userQualification as DriverLicenseQualification).driverLicense.name
        }`,
      );
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.FurtherEducation
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'furtherEducation'] },
        ),
      );
      qualificationName = this.translate.instant(
        `furtherEducation.${
          (userQualification as FurtherEducationQualification).furtherEducation
            .name
        }`,
      );
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.MedicalExamination
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'medicalExamination'] },
        ),
      );
      qualificationName = this.translate.instant(
        `medicalExamination.${
          (userQualification as MedicalExaminationQualification)
            .medicalExamination.name
        }`,
      );
    } else if (qualificationCategoryId === QualificationCategoryIdsMap.More) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'otherQualificationSubCategory'] },
        ),
      );
      qualificationName = this.translate.instant(
        `otherQualification.${
          (userQualification as OtherQualification)
            .otherQualificationSubCategory.name
        }`,
      );
    } else if (
      qualificationCategoryId === QualificationCategoryIdsMap.SafetyCertificate
    ) {
      [userQualification, ...qualificationHistory] = await firstValueFrom(
        this.qualificationsService.getSharedUserQualificationHistory(
          qualificationId,
          qualificationCategoryId,
          { relations: [...relations, 'safetyCertificate'] },
        ),
      );

      qualificationName = (userQualification as SafetyCertificateQualification)
        .safetyCertificate.name;
    }

    return {
      userQualification,
      qualificationHistory,
      qualificationName,
    };
  }
}
